import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { useOnboarding } from '@/features/onboarding/onboarding';
import { enablePushDeviceRegistration } from '@/features/account/pushPreferenceFlow';
import {
  getExpoPushToken,
  getNotificationPermissionStatus,
  requestNotificationPermission,
} from '@/lib/notifications/notifications';
import {
  trackLocationPermission,
  trackNotificationPermission,
  trackOnboardingAbandoned,
  trackOnboardingCompleted,
  trackOnboardingStarted,
} from '@/lib/analytics/posthog';
import { subscribeToAppStateChange } from '@/lib/app/nativeEventSubscriptions';
import { showToast } from '@/lib/useToastStore';
import { cn } from '@/lib/utils';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import * as Location from 'expo-location';
import { router } from 'expo-router';
import {
  BellIcon,
  CalendarDaysIcon,
  ExternalLinkIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TicketPercentIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Linking,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const SA_CENTER = { lat: 29.4241, lng: -98.4936 };
const SA_RADIUS_MILES = 50;

type ScreenID = 'welcome' | 'privacy' | 'location' | 'outside_sa' | 'notifications';

const ALL_SCREENS: ScreenID[] = ['welcome', 'privacy', 'location', 'notifications'];

function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const radius = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isNearSanAntonio(lat: number, lng: number): boolean {
  return haversineMiles(lat, lng, SA_CENTER.lat, SA_CENTER.lng) <= SA_RADIUS_MILES;
}

export default function Onboarding() {
  const isDesktop = useIsDesktop();
  const request = useAuth((state) => state.request);
  const syncMarketLocation = useAuth((state) => state.syncMarketLocation);
  const complete = useOnboarding((state) => state.complete);

  const [screens, setScreens] = React.useState<ScreenID[]>(ALL_SCREENS);
  const [screenIndex, setScreenIndex] = React.useState(0);
  const [locationNeedsSettings, setLocationNeedsSettings] = React.useState(false);
  const startedAtRef = React.useRef(Date.now());
  const finishedRef = React.useRef(false);
  const currentScreenRef = React.useRef<ScreenID>(ALL_SCREENS[0]);
  const anim = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    trackOnboardingStarted();
  }, []);

  React.useEffect(() => {
    currentScreenRef.current = screens[screenIndex] ?? ALL_SCREENS[0];
  }, [screenIndex, screens]);

  React.useEffect(
    () => () => {
      if (!finishedRef.current) {
        trackOnboardingAbandoned(currentScreenRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    refreshLocationPermission().catch(() => undefined);

    return subscribeToAppStateChange((state) => {
      if (state === 'active') {
        refreshLocationPermission().catch(() => undefined);
      }
    });
  }, []);

  async function refreshLocationPermission() {
    const permission = await Location.getForegroundPermissionsAsync();
    setLocationNeedsSettings(permission.status === 'denied' && !permission.canAskAgain);
    if (permission.status === 'granted') {
      setScreens(['welcome', 'privacy', 'notifications']);
    }
  }

  React.useEffect(() => {
    anim.setValue(0);
    Animated.timing(anim, {
      toValue: 1,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [anim, screenIndex]);

  const pageStyle = {
    opacity: anim,
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [12, 0] }) }],
  };

  function advance() {
    setScreenIndex((current) => current + 1);
  }

  async function handleEnableLocation() {
    if (locationNeedsSettings) {
      await Linking.openSettings().catch(() => undefined);
      return;
    }

    const permission = await Location.requestForegroundPermissionsAsync().catch(() => null);
    const status = permission?.status ?? 'denied';

    trackLocationPermission({ granted: status === 'granted', source: 'location_onboarding' });

    if (status !== 'granted') {
      setLocationNeedsSettings(permission?.canAskAgain === false);
      advance();
      return;
    }

    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });
      if (!isNearSanAntonio(location.coords.latitude, location.coords.longitude)) {
        setScreens((current) => {
          const notificationIndex = current.indexOf('notifications');
          if (notificationIndex === -1 || current.includes('outside_sa')) {
            return current;
          }
          const next = [...current];
          next.splice(notificationIndex, 0, 'outside_sa');
          return next;
        });
      }
    } catch {
      // Permission was granted, but the location read can still fail briefly.
    }

    advance();
  }

  async function handleNotifyMe(email: string) {
    request('/waitlist/expansion', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }).catch(() => undefined);
    advance();
  }

  async function handleVisiting() {
    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });
      await syncMarketLocation(location.coords.latitude, location.coords.longitude);
    } catch {
      // The feed can still fall back to San Antonio browse mode.
    }

    request('/me/visiting', {
      method: 'POST',
      body: JSON.stringify({}),
    }).catch(() => undefined);
    advance();
  }

  async function handleEnableNotifications() {
    const result = await enablePushDeviceRegistration({
      getPermissionStatus: getNotificationPermissionStatus,
      requestPermission: requestNotificationPermission,
      getToken: getExpoPushToken,
      registerToken: (token) =>
        request('/me/push-token', {
          method: 'POST',
          body: JSON.stringify({ token }),
        }),
      trackPermission: (granted) => {
        trackNotificationPermission({
          granted,
          source: 'notification_onboarding',
        });
      },
    });
    if (!result.ok && result.reason !== 'permission') {
      showToast({
        message: 'Notifications will finish setting up when your connection is ready.',
      });
    }
    await finish();
  }

  async function finish() {
    finishedRef.current = true;
    await complete();
    trackOnboardingCompleted(
      screens.length,
      Math.round((Date.now() - startedAtRef.current) / 1000)
    );
    router.replace('/(tabs)/events');
  }

  const currentScreen = screens[screenIndex];

  return (
    <SafeAreaView className="flex-1 bg-background">
      <View className={cn('flex-1', isDesktop && 'w-full max-w-[430px] self-center')}>
        <View className="absolute left-0 right-0 top-5 z-10 flex-row justify-center gap-2">
          {screens.map((screen, index) => (
            <View
              key={screen}
              className={cn(
                'h-1.5 rounded-full',
                index === screenIndex ? 'w-6 bg-primary' : 'w-1.5 bg-muted'
              )}
            />
          ))}
        </View>

        {currentScreen === 'welcome' ? <WelcomeScreen pageStyle={pageStyle} onNext={advance} /> : null}
        {currentScreen === 'privacy' ? (
          <PrivacyScreen
            pageStyle={pageStyle}
            onNext={advance}
            onOpenPolicy={() => router.push('/privacy-policy')}
          />
        ) : null}
        {currentScreen === 'location' ? (
          <LocationScreen
            locationNeedsSettings={locationNeedsSettings}
            pageStyle={pageStyle}
            onEnable={handleEnableLocation}
            onSkip={advance}
          />
        ) : null}
        {currentScreen === 'outside_sa' ? (
          <OutsideSanAntonioScreen
            pageStyle={pageStyle}
            onNotifyMe={handleNotifyMe}
            onVisiting={handleVisiting}
            onSkip={advance}
          />
        ) : null}
        {currentScreen === 'notifications' ? (
          <NotificationsScreen
            pageStyle={pageStyle}
            onEnable={handleEnableNotifications}
            onSkip={finish}
          />
        ) : null}
      </View>
    </SafeAreaView>
  );
}

function WelcomeScreen({ pageStyle, onNext }: { pageStyle: object; onNext: () => void }) {
  return (
    <OnboardingFrame pageStyle={pageStyle}>
      <View className="gap-5">
        <View className="gap-3">
          <Text variant="h1" className="text-4xl leading-tight">
            Find the night before it finds you.
          </Text>
          <Text className="text-muted-foreground">
            Radious brings together events, live deals, venues, and the people making San Antonio move.
          </Text>
        </View>

        <View className="gap-3 rounded-xl border border-border bg-card p-4">
          <PreviewRow icon={CalendarDaysIcon} title="Late Set at Jazz TX" detail="Tonight · 10:00 PM" />
          <PreviewRow icon={TicketPercentIcon} title="Pearl all-day happy hour" detail="Live deal nearby" />
          <PreviewRow icon={SparklesIcon} title="Plans tuned to your taste" detail="Discover gets better as you go" />
        </View>
      </View>

      <Button size="lg" className="h-12" onPress={onNext}>
        <Text>Continue</Text>
      </Button>
    </OnboardingFrame>
  );
}

function PrivacyScreen({
  pageStyle,
  onNext,
  onOpenPolicy,
}: {
  pageStyle: object;
  onNext: () => void;
  onOpenPolicy: () => void;
}) {
  return (
    <OnboardingFrame pageStyle={pageStyle}>
      <View className="gap-5">
        <IconBlock icon={ShieldCheckIcon} />
        <View className="gap-3">
          <Text variant="h1" className="text-4xl leading-tight">
            Nearby without being weird.
          </Text>
          <Text className="text-muted-foreground">
            Location helps sort what is close. Your exact location is not shown to venues or other guests.
          </Text>
        </View>
        <View className="gap-3 rounded-xl border border-border bg-card p-4">
          <TrustLine label="Used for" value="nearby events, deals, and map results" />
          <TrustLine label="Not used for" value="public tracking or showing your exact spot" />
          <TrustLine label="Control" value="you can change permissions any time" />
        </View>
      </View>

      <View className="gap-3">
        <Button size="lg" className="h-12" onPress={onNext}>
          <Text>Continue</Text>
        </Button>
        <Button variant="ghost" className="h-11" onPress={onOpenPolicy}>
          <Text>Read privacy policy</Text>
        </Button>
      </View>
    </OnboardingFrame>
  );
}

function LocationScreen({
  locationNeedsSettings,
  pageStyle,
  onEnable,
  onSkip,
}: {
  locationNeedsSettings: boolean;
  pageStyle: object;
  onEnable: () => void;
  onSkip: () => void;
}) {
  return (
    <OnboardingFrame pageStyle={pageStyle}>
      <View className="gap-5">
        <IconBlock icon={MapPinIcon} />
        <View className="gap-3">
          <Text variant="h1" className="text-4xl leading-tight">
            Start with what is close.
          </Text>
          <Text className="text-muted-foreground">
            We will use your location to keep Nearby honest and make Discover less random.
          </Text>
        </View>
      </View>

      <View className="gap-3">
        <Button size="lg" className="h-12" onPress={onEnable}>
          <View className="flex-row items-center gap-2">
            <Text>{locationNeedsSettings ? 'Open Settings' : 'Enable location'}</Text>
            {locationNeedsSettings ? (
              <Icon as={ExternalLinkIcon} size={15} className="text-primary-foreground" />
            ) : null}
          </View>
        </Button>
        <Button variant="ghost" className="h-11" onPress={onSkip}>
          <Text>Not now</Text>
        </Button>
      </View>
    </OnboardingFrame>
  );
}

function NotificationsScreen({
  pageStyle,
  onEnable,
  onSkip,
}: {
  pageStyle: object;
  onEnable: () => void;
  onSkip: () => void;
}) {
  const [alreadyGranted, setAlreadyGranted] = React.useState(false);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    getNotificationPermissionStatus()
      .then((status) => setAlreadyGranted(status === 'granted'))
      .catch(() => undefined);
  }, []);

  return (
    <OnboardingFrame pageStyle={pageStyle}>
      <View className="gap-5">
        <IconBlock icon={BellIcon} />
        <View className="gap-3">
          <Text variant="h1" className="text-4xl leading-tight">
            Keep the plans you care about.
          </Text>
          <Text className="text-muted-foreground">
            Get useful nudges for saved events, followed venues, live deals, and important changes.
          </Text>
        </View>
        <View className="gap-2">
          {['Saved event reminders', 'New posts from followed venues', 'Live deals worth catching'].map((item) => (
            <View key={item} className="rounded-lg border border-border bg-card p-3">
              <Text className="font-medium">{item}</Text>
            </View>
          ))}
        </View>
      </View>

      <View className="gap-3">
        <Button size="lg" className="h-12" onPress={alreadyGranted ? onSkip : onEnable}>
          <Text>{alreadyGranted ? 'Finish' : 'Turn on notifications'}</Text>
        </Button>
        {!alreadyGranted ? (
          <Button variant="ghost" className="h-11" onPress={onSkip}>
            <Text>Maybe later</Text>
          </Button>
        ) : null}
      </View>
    </OnboardingFrame>
  );
}

function OutsideSanAntonioScreen({
  pageStyle,
  onNotifyMe,
  onVisiting,
  onSkip,
}: {
  pageStyle: object;
  onNotifyMe: (email: string) => void;
  onVisiting: () => void;
  onSkip: () => void;
}) {
  const sessionEmail = useAuth((state) => state.session?.me?.user?.email ?? '');
  const [email, setEmail] = React.useState(sessionEmail);
  const [submitted, setSubmitted] = React.useState(false);

  function handleNotifyMe() {
    if (!email.includes('@') || !email.includes('.')) {
      return;
    }
    setSubmitted(true);
    onNotifyMe(email.trim().toLowerCase());
  }

  return (
    <KeyboardAvoidingView
      className="flex-1"
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        keyboardShouldPersistTaps="handled"
        contentContainerClassName="grow">
        <OnboardingFrame pageStyle={pageStyle}>
          <View className="gap-5">
            <IconBlock icon={MapPinIcon} />
            <View className="gap-3">
              <Text variant="h1" className="text-4xl leading-tight">
                Radious is live in San Antonio.
              </Text>
              <Text className="text-muted-foreground">
                Join the list for your city, or keep browsing San Antonio if you are visiting.
              </Text>
            </View>
          </View>

          <View className="gap-3">
            {!submitted ? (
              <>
                <Input
                  value={email}
                  onChangeText={setEmail}
                  placeholder="your@email.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  className="h-12 rounded-lg bg-card"
                />
                <Button size="lg" className="h-12" onPress={handleNotifyMe}>
                  <Text>Notify me when you expand</Text>
                </Button>
              </>
            ) : (
              <View className="h-12 items-center justify-center rounded-lg border border-primary/30 bg-primary/10">
                <Text className="font-semibold text-primary">You are on the list.</Text>
              </View>
            )}
            <Button variant="outline" className="h-12" onPress={onVisiting}>
              <Text>I am visiting San Antonio</Text>
            </Button>
            <Button variant="ghost" className="h-11" onPress={onSkip}>
              <Text>Skip</Text>
            </Button>
          </View>
        </OnboardingFrame>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function OnboardingFrame({
  children,
  pageStyle,
}: {
  children: React.ReactNode;
  pageStyle: object;
}) {
  return (
    <Animated.View className="flex-1 justify-between gap-8 px-6 pb-6 pt-16" style={pageStyle}>
      {children}
    </Animated.View>
  );
}

function IconBlock({ icon }: { icon: LucideIcon }) {
  return (
    <View className="size-12 items-center justify-center rounded-lg border border-border bg-card">
      <Icon as={icon} className="text-primary" size={22} />
    </View>
  );
}

function PreviewRow({
  icon,
  title,
  detail,
}: {
  icon: LucideIcon;
  title: string;
  detail: string;
}) {
  return (
    <View className="flex-row items-center gap-3">
      <View className="size-9 items-center justify-center rounded-md bg-muted">
        <Icon as={icon} className="text-muted-foreground" size={17} />
      </View>
      <View className="flex-1">
        <Text className="font-semibold">{title}</Text>
        <Text variant="small" className="text-muted-foreground">
          {detail}
        </Text>
      </View>
    </View>
  );
}

function TrustLine({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row gap-3">
      <Text variant="small" className="w-20 font-semibold text-muted-foreground">
        {label}
      </Text>
      <Text variant="small" className="flex-1 text-foreground">
        {value}
      </Text>
    </View>
  );
}
